
import { Vue, Options } from "vue-class-component";
import DownloadTemplate from "../components/DownloadTemplateVertical.vue";
import UploadFile from "@/app/ui/components/upload-file/index.vue";
import { BulkBookingController } from "@/app/ui/controllers/BulkBookingController";
import { GTMCommonEvent } from "@/app/infrastructures/misc/gtm-event/common-event";
import {
  bulkTemplateUrl,
  permissionBulkBooking,
  TemplateAudience,
  TemplateScope
} from "./modules/utils";
import SectionFilterClient from "@/app/ui/views/shipment/booking/create-booking-v2/components/section-filter-client.vue";
import { FlagsPermissionBulkBookingV2 } from "@/feature-flags/flags-bulk-booking-v2";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { FlagsBcsBulkDownloadTemplate } from "@/feature-flags/flags-bulk-download-template";

@Options({
  components: {
    DownloadTemplate,
    UploadFile,
    SectionFilterClient
  }
})
export default class BulkBooking extends Vue {
  mounted() {
    this.resetForm();
    this.form.type = this.utilsBulkBooking().permissionPos
      ? "retail"
      : this.canCreateClientBooking
      ? "client"
      : "";
  }

  // route navigation
  goBack() {
    this.$router.push("/shipment/bulk/bulk-v2");
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  templateUrl(audience: TemplateAudience, scope: TemplateScope): string {
    return bulkTemplateUrl({
      audience,
      scope,
      countryCode: this.dataProfile.countryCode
    });
  }

  get canCreateClientBooking(): boolean {
    return (
      this.utilsBulkBooking().permissionClient ||
      this.utilsBulkBooking().permissionInternal ||
      this.utilsBulkBooking().permissionPosClient
    );
  }

  get canSelectBookingType(): boolean {
    return this.utilsBulkBooking().permissionPos || this.canCreateClientBooking;
  }

  get hasBothBookingTypes(): boolean {
    return this.utilsBulkBooking().permissionPos && this.canCreateClientBooking;
  }

  get bookingTypeIndicatorStyle(): Record<string, string> {
    if (!this.hasBothBookingTypes) {
      return { left: "4px", right: "4px" };
    }

    return {
      left: this.form.type === "client" ? "50%" : "4px",
      width: "calc(50% - 4px)"
    };
  }

  bookingTypeClass(type: "retail" | "client"): string[] {
    return [
      "relative z-10 flex flex-1 items-center justify-center gap-2 rounded-full h-8 font-semibold transition-colors duration-200",
      this.form.type === type
        ? "text-white"
        : "bg-white text-gray-lp-500"
    ];
  }

  get templateBookingData() {
    return [
      {
        title: "Retail international booking",
        isButton: true,
        url: this.templateUrl("retail", "international"),
        permission: this.utilsBulkBooking().permissionPos,
        id: "button_download_template_booking_retail_international",
        type: "",
        dataTestId: "bulk-booking-create-button-download-template-retail-international"
      },
      {
        title: "Retail domestic booking",
        isButton: true,
        url: this.templateUrl("retail", "domestic"),
        permission: this.utilsBulkBooking().permissionPos,
        id: "button_download_template_booking_retail_domestic",
        type: "",
        dataTestId: "bulk-booking-create-button-download-template-retail-domestic"
      },
      {
        title: "Client international booking",
        isButton: true,
        url: this.templateUrl("client", "international"),
        permission: this.canCreateClientBooking,
        id: "button_download_template_booking_client_international",
        type: "",
        dataTestId: "bulk-booking-create-button-download-template-client-international"
      },
      {
        title: "Client domestic booking",
        isButton: true,
        url: 
          this.dataProfile.countryCode?.toLowerCase() === "id" 
            ? `${FlagsBcsBulkDownloadTemplate.config_bucket_storage.getValue()}/template_bulk_booking_client.xlsx` 
            : `${FlagsBcsBulkDownloadTemplate.config_bucket_storage_announcement.getValue()}/template/Template+booking+klien+-+EN.xlsx`,
        permission: this.canCreateClientBooking,
        id: "button_download_template_booking_client_domestic",
        type: "",
        dataTestId: "bulk-booking-create-button-download-template-client-domestic"
      }
    ];
  }
  get templateComodityData() {
    return [
      {
        title: "Commodity data",
        isButton: false,
        permission:
          this.utilsBulkBooking().permissionPos ||
          this.utilsBulkBooking().permissionClient ||
          this.utilsBulkBooking().permissionInternal,
        id: "field_download_data_komoditas",
        type: "commodity/export_csv",
        dataTestId: "bulk-booking-create-link-commodity-data"
      },
      {
        title: "District data",
        isButton: false,
        permission:
          this.utilsBulkBooking().permissionPos ||
          this.utilsBulkBooking().permissionClient ||
          this.utilsBulkBooking().permissionInternal,
        id: "field_download_data_kecamatan",
        type: "district/export_csv?status=ACTIVE",
        dataTestId: "bulk-booking-create-link-district-data"
      }
    ];
  }

  get maximumStt() {
    return FlagsPermissionBulkBookingV2.config_bulk_booking_v2_max_stt.getValue();
  }
  
  get maximumFileSize() {
    return FlagsPermissionBulkBookingV2.config_bulk_booking_v2_max_file_MB.getValue();
  }

  get uploadFileCaption(): string {
    return `Format .xlsx. Maximum 500 STT and ${this.maximumFileSize} MB file.<br/>Only the first 500 data rows will be processed.`;
  }

  get form(): any {
    return BulkBookingController.form;
  }

  get utilsBulkBooking() {
    return permissionBulkBooking;
  }

  get isPermissionClient() {
    return this.utilsBulkBooking().permissionClient;
  }

  get isValidateType() {
    const isArchiveType =
      !this.form.type ||
      this.form.type === "Bulk Booking" ||
      (this.form.type === "client" &&
        !this.form.clientName &&
        !this.utilsBulkBooking().permissionClient);
    return isArchiveType;
  }

  get isBulkUploadValid() {
    return (
      !this.form.activityName ||
      this.form.activityName.length < 3 ||
      typeof this.form.archiveFile !== "object" ||
      this.isValidateType
    );
  }

  get messageModalBookingOnProcess() {
    return `Booking "${this.activityNameBooking}" is in progress. Please wait a moment.`;
  }

  get messageModalBookingFailedUpload() {
    return `Booking "${this.activityNameBooking}" was unsuccessful. Please upload the file again.`;
  }

  modalMessageSuccess(data: any) {
    return data?.success && data?.message === "uploading";
  }

  activityNameBooking = "";
  onChangeActivityName(value: string) {
    this.activityNameBooking = value;
  }

  getBookingType(value: string) {
    this.form.type = value;
    this.form.clientName = "";
  }

  resultUpload = false;
  resultErrorUpload = false;
  errorTitle = "";
  errorSubtitle = "";
  btnModalFailed = "";
  btnModalCancel = "";
  errorCode = 0;
  async _onAdd() {
    const resp = await BulkBookingController.uploadBulkBooking({
      activityName: this.form.activityName,
      archiveFile: this.form.archiveFile,
      posClientId:
        this.form.type === "client" && !this.utilsBulkBooking().permissionClient
          ? this.form.clientName.id
          : this.utilsBulkBooking().idAccount,
      posClientType: this.form.type === "client" ? "client" : "pos",
      templateFormat:
        this.form.type === "client" ? "client_template" : "genesis_template",
      versionType: "stt-v2"
    });

    if (this.modalMessageSuccess(resp)) {
      this.resultUpload = true;
      this.resetForm();
    } else {
      const statusCode = resp.response.data?.code;
      this.errorConditionModal(statusCode);
    }
    GTMCommonEvent("upload_bulk_booking_upload_clicked");
  }

  errorConditionModal(codeResp: number) {
    const statusCode = codeResp >= 100 && codeResp <= 104;
    const titleError: any = {
      "100": "There is an empty row!",
      "101": "Booking data exceeds the limit!",
      "102": "Invalid file header!",
      "103": "There is an empty row!",
      "104": "Bulk booking queue is full",
      "105": `Bulk booking file exceeds ${this.maximumFileSize} MB`,
      default: "A system issue occurred"
    };

    const subtitleError: any = {
      "100": "Check the file, complete all rows, and upload it again.",
      "101": "The maximum is 100 rows. Remove some uploaded data.",
      "102": "Check the file and re-upload it using the template format.",
      "103": "Check the file, complete all rows, and upload it again.",
      "104": "Please wait a moment and try again.",
      "105": "Change the file and upload it again.",
      default: 'Click "Reload" or wait a moment.'
    };

    this.errorTitle = titleError[`${codeResp}`] || titleError.default;
    this.errorSubtitle = subtitleError[`${codeResp}`] || subtitleError.default;
    const btnError = codeResp === 105 ? "Upload again" : "Reload";
    this.btnModalFailed = statusCode ? "OK, I understand" : btnError;
    this.btnModalCancel = codeResp === 105 ? "Back" : "";

    this.resultErrorUpload = true;
    this.errorCode = codeResp;
  }

  resetForm() {
    const reset = {
      type: "Bulk Booking",
      activityName: "",
      archiveFile: "",
      clientName: "",
      formatTemplate: "",
    };
    BulkBookingController.setFormAddBulkBooking(reset);
    BulkBookingController.setStatusClearFile(false);
  }

  closePageUploadBulkBooking() {
    this.$router.push("/shipment/bulk/bulk-v2");
  }

  onCloseUploadBulkBooking() {
    this.resultErrorUpload = false;
  }

  onSubmitFailedUploadBulkBooking() {
    this.resultErrorUpload = false;

    if (this.errorCode === 105) {
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement | null;
      fileInput?.click();
    }
  }
}
